import { mkdir, readFile, writeFile } from "node:fs/promises";
import { dirname } from "node:path";

interface Subscriber {
  regex: RegExp;
  handler: (key: string, value: unknown) => void;
}

/**
 * Disk-backed reactive key-value store for plugin state.
 *
 * Keeps an in-memory map that can be flushed to / loaded from a JSON file.
 * Supports glob-style subscriptions (`connector:*`, `*`).
 *
 * State is persisted to `<projectDir>/.skaile/plugin-state.json`.
 * The instance is available on {@link WorkspacePlugin} as the public `store`
 * field after `boot()` resolves.
 *
 * @docLink packages/workspace-plugin/concepts#plugin-store
 */
export class PluginStore {
  private readonly statePath: string;
  private data: Map<string, unknown> = new Map();
  private subscribers: Subscriber[] = [];

  constructor(statePath: string) {
    this.statePath = statePath;
  }

  /** Synchronous read from in-memory map. */
  get<T>(key: string): T | undefined {
    return this.data.get(key) as T | undefined;
  }

  /** Write to in-memory map and notify matching subscribers. */
  set<T>(key: string, value: T): void {
    this.data.set(key, value);
    this.notify(key, value);
  }

  /** Remove key and notify matching subscribers. */
  delete(key: string): void {
    this.data.delete(key);
    this.notify(key, undefined);
  }

  /**
   * Subscribe to changes matching a glob-style pattern.
   * `*` matches any sequence of characters.
   * Returns an unsubscribe function.
   */
  subscribe(pattern: string, handler: (key: string, value: unknown) => void): () => void {
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
    const regexStr = `^${escaped.replace(/\*/g, ".*")}$`;
    const sub: Subscriber = { regex: new RegExp(regexStr), handler };
    this.subscribers.push(sub);

    return () => {
      const idx = this.subscribers.indexOf(sub);
      if (idx !== -1) this.subscribers.splice(idx, 1);
    };
  }

  /** Rehydrate from disk JSON. Missing file = empty store (no throw). */
  async load(): Promise<void> {
    try {
      const raw = await readFile(this.statePath, "utf-8");
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        this.data = new Map(Object.entries(parsed));
      }
    } catch (e: unknown) {
      // Missing file or invalid JSON — start with empty store
    }
  }

  /** Write entire store to disk as JSON. Creates parent dirs if needed. */
  async flush(): Promise<void> {
    const obj = Object.fromEntries(this.data);
    const json = JSON.stringify(obj, null, 2);
    await mkdir(dirname(this.statePath), { recursive: true });
    await writeFile(this.statePath, json, "utf-8");
  }

  private notify(key: string, value: unknown): void {
    for (const sub of this.subscribers) {
      if (sub.regex.test(key)) {
        sub.handler(key, value);
      }
    }
  }
}
